import { WritableDraft } from 'immer';
import { core_v1_DataSource_FieldUtils } from '@antimatterhq/asl-api-ts-v1';
import { deepEqual } from '../../../datasourceUtils';
import {
  DatasourceUpgradeFieldUtilsDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeStatusFieldUtils,
} from '../../datasourceUpgrade.types';
import { getUpgradeFieldStatus } from '../../datasourceUpgradeHelper';
import {
  Datasource,
  Datasource_FieldUtils,
  Datasource_UnreferencedColumns,
} from '@/types/api';
import { filterNulls } from '@/utils/filterNulls';

export const setPreserveUnreferencedFieldsText = (
  state: DatasourceUpgradeFieldUtilsDiff | undefined,
) => {
  if (!state?.unreferencedColumns) {
    return;
  }

  const { unreferencedColumns } = state;

  if (
    !unreferencedColumns.preserve.currentPresetValue?.preserve &&
    unreferencedColumns.preserve.upgradePresetValue?.preserve
  ) {
    unreferencedColumns.preserve.title = 'Preserve unreferenced fields';
    unreferencedColumns.preserve.description =
      'Unreferenced fields are now preserved';
    return;
  }

  if (
    unreferencedColumns.preserve.currentPresetValue?.preserve &&
    !unreferencedColumns.preserve.upgradePresetValue?.preserve
  ) {
    unreferencedColumns.preserve.title = 'Preserve unreferenced fields';
    unreferencedColumns.preserve.description =
      'Unreferenced fields are no longer preserved';
    return;
  }

  if (
    !deepEqual(
      unreferencedColumns.omitColumns.currentPresetValue,
      unreferencedColumns.omitColumns.upgradePresetValue,
    )
  ) {
    unreferencedColumns.omitColumns.title = 'Omit unreferenced fields';
    unreferencedColumns.omitColumns.description =
      'Omitted fields for preserved unreferenced fields changed';
  }

  if (
    !unreferencedColumns.embedColumn.currentPresetValue &&
    unreferencedColumns.embedColumn.upgradePresetValue
  ) {
    unreferencedColumns.embedColumn.title = 'Embed unreferenced fields';
    unreferencedColumns.embedColumn.description =
      'Unreferenced fields are now embedded in a new column';
  }

  if (
    unreferencedColumns.embedColumn.currentPresetValue &&
    !unreferencedColumns.embedColumn.upgradePresetValue
  ) {
    unreferencedColumns.embedColumn.title = 'Embed unreferenced fields';
    unreferencedColumns.embedColumn.description =
      'Unreferenced fields are no longer embedded in a new column';
  }

  if (
    unreferencedColumns.embedColumnType.currentPresetValue !==
    unreferencedColumns.embedColumnType.upgradePresetValue
  ) {
    unreferencedColumns.embedColumnType.title = 'Embed column type';
    unreferencedColumns.embedColumnType.description = `Unreferenced fields are now embedded in a new column with the type ${unreferencedColumns.embedColumnType.upgradePresetValue}`;
  }

  if (
    unreferencedColumns.duplicatePrefix.currentPresetValue !==
    unreferencedColumns.duplicatePrefix.upgradePresetValue
  ) {
    unreferencedColumns.duplicatePrefix.title = 'Duplicate prefix';
    unreferencedColumns.duplicatePrefix.description =
      'Duplicate prefix for unreferenced fields changed';
  }
};

export const setSilverUnreferencedColumnsChanges = (
  datasource: WritableDraft<Datasource>,
  unreferencedColumns: core_v1_DataSource_FieldUtils['unreferencedColumns'],
  tableName?: string,
) => {
  const table =
    datasource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
      ({ name }) => name === tableName,
    );

  if (!table) {
    throw new Error(`Table ${tableName} not found in silver`);
  }

  table.utils = {
    ...table.utils,
    unreferencedColumns: {
      ...table.utils?.unreferencedColumns,
      ...unreferencedColumns,
    },
  };
};

export const setPreTransformUnreferencedColumnsChanges = (
  datasource: WritableDraft<Datasource>,
  unreferencedColumns: core_v1_DataSource_FieldUtils['unreferencedColumns'],
) => {
  // eslint-disable-next-line no-param-reassign
  datasource.spec = {
    ...datasource.spec,
    silver: {
      ...datasource.spec.silver,
      preTransform: {
        ...datasource.spec.silver?.preTransform,
        utils: {
          ...datasource.spec.silver?.preTransform?.utils,
          unreferencedColumns: {
            ...datasource.spec.silver?.preTransform?.utils?.unreferencedColumns,
            ...unreferencedColumns,
          },
        },
      },
    },
  };
};

export const setDuplicatePrefixChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
  stage: 'preTransform' | 'silver',
  tableName?: string,
) => {
  const unreferencedColumns = {
    duplicatePrefix: value,
  };
  if (stage === 'preTransform') {
    setPreTransformUnreferencedColumnsChanges(datasource, unreferencedColumns);
  } else {
    setSilverUnreferencedColumnsChanges(
      datasource,
      unreferencedColumns,
      tableName,
    );
  }
};

export const setEmbedColumnChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
  stage: 'preTransform' | 'silver',
  tableName?: string,
) => {
  const unreferencedColumns = {
    embedColumn: value,
  };
  if (stage === 'preTransform') {
    setPreTransformUnreferencedColumnsChanges(datasource, unreferencedColumns);
  } else {
    setSilverUnreferencedColumnsChanges(
      datasource,
      unreferencedColumns,
      tableName,
    );
  }
};

export const setEmbedColumnTypeChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
  stage: 'preTransform' | 'silver',
  tableName?: string,
) => {
  const unreferencedColumns = {
    embedColumnType: value,
  };
  if (stage === 'preTransform') {
    setPreTransformUnreferencedColumnsChanges(datasource, unreferencedColumns);
  } else {
    setSilverUnreferencedColumnsChanges(
      datasource,
      unreferencedColumns,
      tableName,
    );
  }
};

export const setOmitColumnsChanges = (
  datasource: WritableDraft<Datasource>,
  value: string[] | undefined,
  stage: 'preTransform' | 'silver',
  tableName?: string,
) => {
  const unreferencedColumns = {
    omitColumns: value ? [...value] : undefined,
  };

  if (stage === 'preTransform') {
    setPreTransformUnreferencedColumnsChanges(datasource, unreferencedColumns);
  } else {
    setSilverUnreferencedColumnsChanges(
      datasource,
      unreferencedColumns,
      tableName,
    );
  }
};

export const setPreserveUnreferencedColumnsChanges = (
  datasource: WritableDraft<Datasource>,
  value: Datasource_UnreferencedColumns | undefined,
  stage: 'preTransform' | 'silver',
  tableName?: string,
) => {
  if (stage === 'preTransform') {
    setPreTransformUnreferencedColumnsChanges(datasource, value);
  } else {
    setSilverUnreferencedColumnsChanges(datasource, value, tableName);
  }
};

export const getUnreferencedColumnsStatuses = ({
  customUtils,
  upgradeVersionUtils,
  currentVersionUtils,
}: {
  customUtils?: Datasource_FieldUtils;
  upgradeVersionUtils?: Datasource_FieldUtils;
  currentVersionUtils?: Datasource_FieldUtils;
}) => {
  const statuses: DatasourceUpgradeStatusFieldUtils['unreferencedColumns'] = {
    preserve: getUpgradeFieldStatus({
      currentPresetValue: currentVersionUtils?.unreferencedColumns?.preserve,
      upgradePresetValue: upgradeVersionUtils?.unreferencedColumns?.preserve,
      currentValue: customUtils?.unreferencedColumns?.preserve,
    }),
    embedColumn: getUpgradeFieldStatus({
      currentPresetValue: currentVersionUtils?.unreferencedColumns?.embedColumn,
      upgradePresetValue: upgradeVersionUtils?.unreferencedColumns?.embedColumn,
      currentValue: customUtils?.unreferencedColumns?.embedColumn,
    }),
    embedColumnType: getUpgradeFieldStatus({
      currentPresetValue:
        currentVersionUtils?.unreferencedColumns?.embedColumnType,
      upgradePresetValue:
        upgradeVersionUtils?.unreferencedColumns?.embedColumnType,
      currentValue: customUtils?.unreferencedColumns?.embedColumnType,
    }),
    duplicatePrefix: getUpgradeFieldStatus({
      currentPresetValue:
        currentVersionUtils?.unreferencedColumns?.duplicatePrefix,
      upgradePresetValue:
        upgradeVersionUtils?.unreferencedColumns?.duplicatePrefix,
      currentValue: customUtils?.unreferencedColumns?.duplicatePrefix,
    }),
    omitColumns: getUpgradeFieldStatus({
      currentPresetValue: filterNulls(
        currentVersionUtils?.unreferencedColumns?.omitColumns,
      ),
      upgradePresetValue: filterNulls(
        upgradeVersionUtils?.unreferencedColumns?.omitColumns,
      ),
      currentValue: filterNulls(customUtils?.unreferencedColumns?.omitColumns),
    }),
  };

  return statuses;
};

const getDraftUtils = ({
  draft,
  stage,
  tableName,
}: {
  draft: WritableDraft<DatasourceUpgradeStatus>;
  stage: 'preTransform' | 'silver';
  tableName?: string;
}) => {
  if (stage === 'preTransform') {
    return draft.spec.silver.preTransform.utils;
  }

  const table = draft.spec.silver.transform.find(
    ({ name }) => name === tableName,
  );

  if (!table) {
    throw new Error(`Table ${tableName} not found in ${stage}`);
  }

  return table.utils;
};

export const getUnreferencedColumnsDiff = ({
  status,
  currentUtils,
  upgradeUtils,
  upgradeVersionUtils,
  currentVersionUtils,
  stage,
  tableName,
}: {
  status: DatasourceUpgradeStatusFieldUtils;
  currentUtils?: Datasource_FieldUtils;
  upgradeUtils?: Datasource_FieldUtils;
  upgradeVersionUtils?: Datasource_FieldUtils;
  currentVersionUtils?: Datasource_FieldUtils;
  stage: 'preTransform' | 'silver';
  tableName?: string;
}) => {
  const diff: DatasourceUpgradeFieldUtilsDiff['unreferencedColumns'] = {
    preserve: {
      currentPresetValue: currentVersionUtils?.unreferencedColumns || undefined,
      upgradePresetValue: upgradeVersionUtils?.unreferencedColumns || undefined,
      currentValue: currentUtils?.unreferencedColumns || undefined,
      upgradeValue: upgradeUtils?.unreferencedColumns || undefined,
      status: status.unreferencedColumns.preserve,
      updateStatus: ({ draft, status: newStatus }) =>
        (getDraftUtils({
          draft,
          stage,
          tableName,
        }).unreferencedColumns.preserve = newStatus),
      type: 'preserveUnreferencedColumns',
      stage,
      tableName,
      setChanges: ({ datasource, value }) =>
        setPreserveUnreferencedColumnsChanges(
          datasource,
          value,
          stage,
          tableName,
        ),
    },
    embedColumn: {
      currentPresetValue:
        currentVersionUtils?.unreferencedColumns?.embedColumn || undefined,
      upgradePresetValue:
        upgradeVersionUtils?.unreferencedColumns?.embedColumn || undefined,
      currentValue: currentUtils?.unreferencedColumns?.embedColumn || undefined,
      upgradeValue: upgradeUtils?.unreferencedColumns?.embedColumn || undefined,
      status: status.unreferencedColumns.embedColumn,
      updateStatus: ({ draft, status: newStatus }) =>
        (getDraftUtils({
          draft,
          stage,
          tableName,
        }).unreferencedColumns.embedColumn = newStatus),
      type: 'string',
      inputType: 'textbox',
      setChanges: ({ datasource, value }) =>
        setEmbedColumnChanges(datasource, value, stage, tableName),
    },
    embedColumnType: {
      currentPresetValue:
        currentVersionUtils?.unreferencedColumns?.embedColumnType || undefined,
      upgradePresetValue:
        upgradeVersionUtils?.unreferencedColumns?.embedColumnType || undefined,
      currentValue:
        currentUtils?.unreferencedColumns?.embedColumnType || undefined,
      upgradeValue:
        upgradeUtils?.unreferencedColumns?.embedColumnType || undefined,
      status: status.unreferencedColumns.embedColumnType,
      updateStatus: ({ draft, status: newStatus }) =>
        (getDraftUtils({
          draft,
          stage,
          tableName,
        }).unreferencedColumns.embedColumnType = newStatus),
      type: 'string',
      inputType: 'fixed',
      setChanges: ({ datasource, value }) =>
        setEmbedColumnTypeChanges(datasource, value, stage, tableName),
    },
    duplicatePrefix: {
      currentPresetValue:
        currentVersionUtils?.unreferencedColumns?.duplicatePrefix || undefined,
      upgradePresetValue:
        upgradeVersionUtils?.unreferencedColumns?.duplicatePrefix || undefined,
      currentValue:
        currentUtils?.unreferencedColumns?.duplicatePrefix || undefined,
      upgradeValue:
        upgradeUtils?.unreferencedColumns?.duplicatePrefix || undefined,
      status: status.unreferencedColumns.duplicatePrefix,
      updateStatus: ({ draft, status: newStatus }) =>
        (getDraftUtils({
          draft,
          stage,
          tableName,
        }).unreferencedColumns.duplicatePrefix = newStatus),
      type: 'string',
      inputType: 'textbox',
      setChanges: ({ datasource, value }) =>
        setDuplicatePrefixChanges(datasource, value, stage, tableName),
    },
    omitColumns: {
      currentPresetValue: filterNulls(
        currentVersionUtils?.unreferencedColumns?.omitColumns,
      ),
      upgradePresetValue: filterNulls(
        upgradeVersionUtils?.unreferencedColumns?.omitColumns,
      ),
      currentValue: filterNulls(currentUtils?.unreferencedColumns?.omitColumns),
      upgradeValue: filterNulls(upgradeUtils?.unreferencedColumns?.omitColumns),
      status: status.unreferencedColumns.omitColumns,
      updateStatus: ({ draft, status: newStatus }) =>
        (getDraftUtils({
          draft,
          stage,
          tableName,
        }).unreferencedColumns.omitColumns = newStatus),
      type: 'strings',
      setChanges: ({ datasource, value }) =>
        setOmitColumnsChanges(datasource, value, stage, tableName),
    },
  };

  return diff;
};
